// @ts-nocheck
import './agGrid.scss';
// import "ag-grid-community/styles/ag-grid.css";
// import "ag-grid-community/styles/ag-theme-alpine.css";
// import "ag-grid-enterprise";

import { AgGridReact } from 'ag-grid-react';
import { Empty } from 'antd';
import { getGridData, getGridFiltersPivotMode, getGridMetadata, getTotalCount } from 'models/dynamicGrid/selector';
import { getTableColumnsInfo } from 'models/tableColumns/selector';
import Stack from 'molecules/stack/stack';
import StackItem from 'molecules/stackItem/stackItem';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getChildTableColumnDefinitions, getColumnDefinitions } from './columnDefinitions';
import { PAGE_SIZE, GRID_EVENTS } from './constants';
import { useAgGrid } from '../../../hooks/useAgGrid';
import { getActionColumn, getToolPanelConfig } from './queries';
import { getWebAppSettings } from 'models/user/selector';
import { Events } from 'molecules/ReactGrid/agGrid/utils/events';
import { FilterTags } from './filterTags/filterTags';
import { formatThousands } from 'utils/utilities';
import { Dot9loader } from 'molecules/dot9loader';
import { usePrefetchOrderExplanations } from './orderExplainer/usePrefetchOrderExplanations';
import OrderExplainerDrawer from './orderExplainer/orderExplainerDrawer';
import {
	ColDef,
	ColGroupDef,
	GridApi,
	GridOptions,
	GridReadyEvent,
	IServerSideDatasource,
	IServerSideGetRowsRequest,
	ModuleRegistry,
	RowModelType,
	ValidationModule,
	RowApiModule,
	ColumnApiModule,
	TextEditorModule,
	NumberEditorModule,
	CustomFilterModule,
	HighlightChangesModule,
	ColumnAutoSizeModule,
	PaginationModule,
	CustomEditorModule,
	CellStyleModule,
	Theme,
	themeQuartz,
	TextFilterModule,
	RowStyleModule,
	RenderApiModule,
	PaginationChangedEvent,
	ClientSideRowModelModule,
} from 'ag-grid-community';
import {
	ColumnMenuModule,
	ColumnsToolPanelModule,
	ContextMenuModule,
	ServerSideRowModelModule,
	MasterDetailModule,
	FiltersToolPanelModule,
	TooltipModule,
	RowGroupingModule,
	RowGroupingPanelModule,
	ServerSideRowModelApiModule,
	CellSelectionModule,
	SetFilterModule,
	MultiFilterModule,
	ClipboardModule,
} from 'ag-grid-enterprise';
import { table } from 'console';
import { ORDER_PIVOT_TABLES } from 'pages/orders/constants';
ModuleRegistry.registerModules([
	ClipboardModule,
	RenderApiModule,
	RowStyleModule,
	TextFilterModule,
	SetFilterModule,
	MultiFilterModule,
	ColumnsToolPanelModule,
	ColumnMenuModule,
	ContextMenuModule,
	ServerSideRowModelModule,
	MasterDetailModule,
	RowApiModule,
	ColumnApiModule,
	FiltersToolPanelModule,
	TooltipModule,
	RowGroupingModule,
	RowGroupingPanelModule,
	TextEditorModule,
	NumberEditorModule,
	CustomFilterModule,
	HighlightChangesModule,
	ServerSideRowModelApiModule,
	ColumnAutoSizeModule,
	PaginationModule,
	CustomEditorModule,
	CellStyleModule,
	CellSelectionModule,
	...(process.env.NODE_ENV !== 'production' ? [ValidationModule] : []),
]);
const columnTypes = {
	editableColumn: {
		editable: (params) => {
			return params.colDef.editable;
		},
		cellStyle: (params) => {
			if (params.colDef.editable) {
				return {
					cursor: 'text',
				};
			}
		},
	},
};
const primaryColour = '#2e5d9c';
const bgSpecialColor = '#92baf3ff';

const customTheme = themeQuartz.withParams({
	sideButtonSelectedUnderlineColor: primaryColour,
	sideButtonSelectedBorder: false,
	sideBarPanelWidth: 330,
	accentColor: primaryColour,
	headerFontWeight: 700,
	selectedRowBackgroundColor: bgSpecialColor,
	rowHoverColor: bgSpecialColor,
	// rowVerticalPaddingScale: 0.2,
});

const AgGrid = (props) => {
	const {
		tableId,
		selectRowKey,
		isStorageEnabled,
		config,
		onDomRef,
		childCustomActions,
		childCellRendererParams = null,
	} = props;

	const {
		gridActions,
		customActions,
		childGridActions,
		childGridCustomActions,
		enableSelection = true,
		rowGroupPanelShow = true,
		rowPivotPanelShow,
		defaultRowGroupColumn,
		defaultRowGroupColumnDisplayOverride,
		isViewImplementationOverride,
		isParentSelectionEnabled,
		isUploadGrid,
		groupLockGroupColumns = 0,
	} = config;

	const gridRef = useRef();
	const childGridRef = useRef(null);
	const gridMeta = useSelector((state) => getGridMetadata(state, tableId));
	const gridAutoSizeRef = useRef(false);
	const [gridApi, setGridApi] = useState(null);

	const isGridFilterPivotMode = useSelector((state) => getGridFiltersPivotMode(state, tableId));

	const pivotMode = tableId === 115 ? false : isGridFilterPivotMode;

	const totalCount = useSelector((state) => getTotalCount(state, tableId));
	const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);

	const tableColumns = useSelector((state) => getTableColumnsInfo(state, tableId));

	const tableData = useSelector((state) => getGridData(state, tableId));

	let childTableColumns = useSelector((state) =>
		gridMeta?.ChildTableID ? getTableColumnsInfo(state, gridMeta?.ChildTableID) : []
	);

	const isViewRLA = useSelector((state) => getWebAppSettings(state)?.IsAclRowLevelAccessEnabled);

	const theme = useMemo<Theme | 'legacy'>(() => {
		return customTheme;
	}, []);

	const tabToNextCell = useCallback((params: TabToNextCellParams): CellPosition | null => {
		const previousCell = params.previousCellPosition;
		const renderedRowCount = params.api!.getDisplayedRowCount();
		const lastRowIndex = previousCell.rowIndex;
		let nextRowIndex = params.backwards ? lastRowIndex - 1 : lastRowIndex + 1;
		if (nextRowIndex < 0) {
			nextRowIndex = -1;
		}
		if (nextRowIndex >= renderedRowCount) {
			nextRowIndex = renderedRowCount - 1;
		}
		const result = {
			rowIndex: nextRowIndex,
			column: previousCell.column,
			rowPinned: previousCell.rowPinned,
		};
		return result;
	}, []);

	const {
		onColumnMoved,
		onColumnPinned,
		onColumnVisible,
		onColumnResized,
		onUpdateCell,
		onSelectionChanged,
		onClearSelectAll,
		onSelectAllClick,
		onColumnPivotModeChanged,
		onModelUpdated: originalOnModelUpdated,
		onRowGroupOpened,
		refreshTimestamp,
		isGroupingEnabled,
		loading,
		onGridReady: originalOnGridReadyFromHook,
		onChildGridReady,
		selectedInfo,
		getContextMenuItems,
		onViewportChanged,
		onColumnMenuVisibleChanged,
		onCellEditRequest,
		isClientSide,
		clientRowData,
	} = useAgGrid({
		tableId,
		gridRef,
		childGridRef,
		childTableId: gridMeta?.ChildTableID,
		selectRowKey,
		isGridFilterPivotMode,
		gridMeta,
		tableColumns,
		onDomRef,
		isStorageEnabled,
		isViewImplementationOverride,
		isViewRLA,
		isParentSelectionEnabled,
	});

	// Wrap onGridReady to capture gridApi for prefetching
	const onGridReady = useCallback((params: GridReadyEvent) => {
		setGridApi(params.api);
		if (originalOnGridReadyFromHook) {
			originalOnGridReadyFromHook(params);
		}
	}, [originalOnGridReadyFromHook]);

	// Prefetch order explanations for visible rows (only for tableId 115)
	usePrefetchOrderExplanations({
		gridApi: gridApi,
		enabled: tableId === 115,
		delay: 1000,
	});

	// Helper to determine if any groups are expanded (must be after useAgGrid to access isGroupingEnabled)
	const [hasExpandedGroups, setHasExpandedGroups] = useState(false);

	// Update hasExpandedGroups when groups are expanded/collapsed
	useEffect(() => {
		if (!gridRef?.current?.api || !isGroupingEnabled) {
			setHasExpandedGroups(false);
			return;
		}

		const checkExpanded = () => {
			let hasExpanded = false;
			try {
				gridRef.current.api.forEachNode((node) => {
					if (node.group && node.expanded) {
						hasExpanded = true;
						return false; // Break early
					}
				});
			} catch (e) {
				// Silently handle errors
			}
			setHasExpandedGroups(hasExpanded);
		};

		checkExpanded();

		// Listen to row group opened/closed events
		const api = gridRef.current.api;
		api.addEventListener('rowGroupOpened', checkExpanded);

		return () => {
			api.removeEventListener('rowGroupOpened', checkExpanded);
		};
	}, [gridRef, isGroupingEnabled, refreshTimestamp]);

	// Show loading during: initial load, probe phase, OR client-side data not yet loaded
	const isDataReady = isClientSide === null ? false : isClientSide ? clientRowData !== null : true; // server-side is always "ready" (datasource handles it)

	// Function to apply preselected row IDs to currently loaded nodes
	const applyPreSelectedRows = useCallback(
		(api) => {
			const preSelectedIds = config?.preSelectedRowIds || [];
			if (!preSelectedIds.length) return;

			api.forEachNode((node) => {
				const rowId = node.data?.[selectRowKey];
				if (preSelectedIds.includes(rowId) && !node.isSelected()) {
					node.setSelected(true);
				}
			});
		},
		[config?.preSelectedRowIds, selectRowKey]
	);

	// Wrap onModelUpdated to also apply preselected rows when new data loads
	const onModelUpdated = useCallback(
		(params) => {
			// Call original onModelUpdated first
			if (originalOnModelUpdated) {
				originalOnModelUpdated(params);
			}
			// Then apply preselected rows to newly loaded data
			applyPreSelectedRows(params.api);
		},
		[originalOnModelUpdated, applyPreSelectedRows]
	);

	const gridStyle = useMemo(
		() => ({
			height: '100%',
			width: '100%',
			minHeight: 0,
			minWidth: 0,
			flex: 1,
			display: loading ? 'none' : 'block',
		}),
		[loading]
	);

	const handleColumnResized = (params) => {
		if (params.source === 'autosizeColumns') {
			gridAutoSizeRef.current = true;
		}
		onColumnResized(params);
	};

	const columnDefs = useMemo(() => {
		const colDefs = gridRef && gridRef?.current?.api?.getColumnDefs();
		const getAllColumns = getColumnDefinitions(
			tableColumns,
			tableId,
			colDefs,
			refreshTimestamp,
			{
				isPivotViewEnabled: rowPivotPanelShow,
				isPivotMode: gridRef?.current?.props?.pivotMode,
				defaultRowGroupColumn,
				defaultRowGroupColumnDisplayOverride,
				isUploadGrid: isUploadGrid,
			},
			gridAutoSizeRef
		);

		let columns = getAllColumns;
		if (gridActions || customActions) {
			columns?.push(
				getActionColumn({
					tableId,
					gridRef,
					customActions,
					isGroupingEnabled,
					gridConfig: config,
				})
			);
		}
		return columns;
	}, [
		tableColumns,
		tableId,
		refreshTimestamp,
		rowPivotPanelShow,
		defaultRowGroupColumn,
		defaultRowGroupColumnDisplayOverride,
		isUploadGrid,
		gridActions,
		customActions,
		isGroupingEnabled,
		config,
		gridAutoSizeRef,
	]);

	const childColumnDefs = useMemo(() => {
		if (!gridMeta?.ChildTableID) {
			return;
		}
		let columns = getChildTableColumnDefinitions(
			childTableColumns,
			gridMeta?.ChildTableID,
			childCustomActions,
			childCellRendererParams
		);

		if (childGridActions || childGridCustomActions) {
			columns?.push(
				getActionColumn({
					tableId: gridMeta?.ChildTableID,
					childGridRef,
					customActions: childGridCustomActions,
				})
			);
		}
		return columns;
	}, [
		gridMeta?.ChildTableID,
		childTableColumns,
		childCustomActions,
		childCellRendererParams,
		childGridActions,
		childGridCustomActions,
		// rowError,
		// rowSuccess
	]);

	const sideBar = useMemo(() => {
		return getToolPanelConfig({
			suppressPivotMode: !rowPivotPanelShow,
			rowGroupPanelShow,
			rowPivotPanelShow,
			tableId,
		});
	}, [rowGroupPanelShow, rowPivotPanelShow, tableId]);

	const getMainMenuItems = (params) => {
		params.defaultItems.pop();
		return params.defaultItems;
	};
	const rowStyle = useMemo(() => {
		return { background: 'transparent' };
	}, []);

	const getRowStyle = useCallback(
		(params) => {
			const editingCells = params.api.getEditingCells();
			const isRowBeingEdited = editingCells.find((cell) => cell.rowIndex === params.node.rowIndex);

			if (isRowBeingEdited) {
				return { background: bgSpecialColor, color: '#fff' }; // highlight whole row
			}
			return null; // fallback to default styling
		},
		[tableId]
	);
	const onChildPaginationChanged = (params: PaginationChangedEvent) => {
		if (params.newPage || params.newPageSize) {
			const page = params.api.paginationGetCurrentPage();
			if (page === 0) {
				params.api.refreshServerSide({ purge: true });
			}
		}
	};

	const onFirstDataRendered = useCallback(
		(params) => {
			applyPreSelectedRows(params.api);
		},
		[applyPreSelectedRows]
	);

	const generateRandomId = (length: number = 9): string => {
		return Math.random()
			.toString(36)
			.substring(2, 2 + length);
	};

	const detailCellRendererParams = useMemo(() => {
		return (parentParams) => {
			if (!gridMeta?.ChildTableID) {
				return;
			}
			return {
				detailGridOptions: {
					ref: childGridRef,
					rowModelType: 'serverSide',
					onColumnMoved,
					onColumnPinned,
					onColumnResized,
					onColumnVisible,
					onGridReady: onChildGridReady,
					columnDefs: childColumnDefs,
					paginateChildRows: true,
					pagination: true,
					rowHeight: 25,
					wrapText: true,
					headerHeight: 32,
					paginationPageSize: PAGE_SIZE,
					serverSideInfiniteScroll: true,
					serverSideInitialRowCount: PAGE_SIZE,
					maxConcurrentDatasourceRequests: 3,
					cacheBlockSize: PAGE_SIZE,
					autoHeight: true,
					getMainMenuItems,
					// suppressGroupRowsSticky: true,
					// groupHideOpenParents: true,
					onCellValueChanged: onUpdateCell,
					onPaginationChanged: onChildPaginationChanged,
					tabToNextCell,
					context: {
						tableId: gridMeta?.ChildTableID,
						parentRowData: parentParams,
					},
					defaultColDef: {
						singleClickEdit: true,
						wrapHeaderText: true,
					},
				},
			};
		};
	}, [
		gridMeta?.ChildTableID,
		onChildGridReady,
		onColumnMoved,
		onColumnPinned,
		onColumnResized,
		onColumnVisible,
		...(tableId !== 107 ? [childColumnDefs] : []),
	]);

	// Update expanded detail grids when column definitions change
	useEffect(() => {
		if (!gridRef?.current || !gridMeta?.ChildTableID || !childColumnDefs) {
			return;
		}

		// Update all expanded detail grids with new column definitions
		gridRef?.current?.api?.forEachNode((node) => {
			if (node.expanded && node.detailNode?.detailGridInfo?.api) {
				const detailApi = node.detailNode.detailGridInfo.api;

				try {
					detailApi.setColumnDefs(childColumnDefs);
				} catch (error) {
					// Silently handle errors
				}
			}
		});
	}, [gridRef, gridMeta?.ChildTableID, childColumnDefs]);

	const autoGroupColumnDef = useMemo(() => {
		return {
			minWidth: 250,
			sortable: true,
			filter: true,
		};
	}, []);

	const isRowSelectable = useCallback(
		(node) => {
			if (!node.data) return false;

			const key = node.data?.[selectRowKey];
			if (!key) return false;

			if (selectedInfo?.isSelectAll) {
				const deselectedRowKeys = selectedInfo?.deselectedRowKeys || [];
				return !deselectedRowKeys.includes(key);
			}
			const selectedKeys = selectedInfo?.selectedRowKeys || [];
			return selectedKeys.includes(key);
		},
		[selectedInfo, selectRowKey]
	);

	const rowSelection = useMemo(() => {
		if (!enableSelection || !selectRowKey) return undefined;
		const hideCheckbox = ORDER_PIVOT_TABLES.includes(tableId);
		return {
			mode: 'multiRow',
			groupSelects: 'descendants',
			checkboxes: !hideCheckbox,
			headerCheckbox: !hideCheckbox,
		};
	}, [enableSelection, selectRowKey, tableId]);

	if (!columnDefs.length || !gridMeta) {
		return null;
	}

	const defaultColDef = {
		suppressMovable: true,
		enableCellChangeFlash: true,
		context: { tableId },
		singleClickEdit: true,
		wrapHeaderText: true,
	};

	const onCellKeyDown = (params) => {
		if (tableId !== 115) return;
		const { api, event, column, node } = params;
		const key = event.key;
		if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) {
			return;
		}
		event.preventDefault();
		const focusedCell = api.getFocusedCell();
		if (!focusedCell) return;
		let nextRowIndex = focusedCell.rowIndex;
		let nextColumn = focusedCell.column;
		const allDisplayedColumns = api.getAllDisplayedColumns();
		const colIndex = allDisplayedColumns.indexOf(focusedCell.column);

		switch (key) {
			case 'ArrowUp':
				nextRowIndex = focusedCell.rowIndex - 1;
				break;
			case 'ArrowDown':
				nextRowIndex = focusedCell.rowIndex + 1;
				break;
			case 'ArrowLeft':
				nextColumn = allDisplayedColumns[colIndex - 1];
				break;
			case 'ArrowRight':
				nextColumn = allDisplayedColumns[colIndex + 1];
				break;
		}
		if (!nextColumn || nextRowIndex < 0) return;

		const oldRowIndex = focusedCell.rowIndex;
		const oldRowNode = oldRowIndex >= 0 ? api.getDisplayedRowAtIndex(oldRowIndex) : null;

		api.stopEditing();
		if (oldRowNode && oldRowIndex !== nextRowIndex) {
			api.redrawRows({ rowNodes: [oldRowNode] });
		}

		api.setFocusedCell(nextRowIndex, nextColumn);
		api.startEditingCell({
			rowIndex: nextRowIndex,
			colKey: nextColumn.getId(),
		});
	};


	return (
		<div style={containerStyle} className="app-grid">
			{/* {tableId === 1 && <OrderExplainerDrawer />} */}
			<Stack isVertical>
				{selectRowKey && (
					<StackItem>
						{/* Show selection info only if 'Select All' is active, or any rows/groups are selected */}
						{(selectedInfo?.isSelectAll ||
							!!selectedInfo?.selectedRowKeys?.length ||
							!!selectedInfo?.rowGroup?.length) && (
							<div className="select-info">
								{selectedInfo?.isSelectAll ? (
									<>
										{/* All rows selected case */}
										{!!selectedInfo?.selectedRowKeys?.length && !selectedInfo?.rowGroup?.length && (
											<>All {formatThousands(totalCount)} rows are selected. </>
										)}

										{/* All groups selected case */}
										{!!selectedInfo?.rowGroup?.length && !selectedInfo?.selectedRowKeys?.length && (
											<>All {formatThousands(totalCount)} groups are selected. </>
										)}

										{/* Both rows and groups selected */}
										{!!selectedInfo?.selectedRowKeys?.length &&
											!!selectedInfo?.rowGroup?.length && (
												<>
													All {formatThousands(selectedInfo.selectedRowKeys.length)} rows and{' '}
													{formatThousands(selectedInfo?.rowGroup?.length)} groups are
													selected.{' '}
												</>
											)}

										{/* Clear all selection */}
										<span className="select-info--action" onClick={onClearSelectAll}>
											Clear selection.
										</span>
									</>
								) : (
									<>
										{/* UX Rule: Case A - Grouped but not expanded: show group count only */}
										{/* UX Rule: Case B - Not grouped: show row count only */}
										{/* UX Rule: Case C - Grouped and expanded: show row count only (leaf nodes) */}
										{(() => {
											// Get actual selected leaf nodes from grid API when groups are expanded
											let actualSelectedRowCount = selectedInfo?.selectedRowKeys?.length || 0;
											if (isGroupingEnabled && hasExpandedGroups && gridRef?.current?.api) {
												try {
													const selectedNodes = gridRef.current.api.getSelectedNodes();
													// Count only leaf nodes (non-group nodes)
													actualSelectedRowCount = selectedNodes.filter(
														(n) => !n.group && n.data?.[selectRowKey]
													).length;
												} catch (e) {
													// Fallback to Redux state
													actualSelectedRowCount = selectedInfo?.selectedRowKeys?.length || 0;
												}
											}

											if (
												isGroupingEnabled &&
												!hasExpandedGroups &&
												selectedInfo?.rowGroup?.length > 0
											) {
												// Case A: Grouped, not expanded - show group count
												return (
													<span>
														{formatThousands(selectedInfo.rowGroup.length)} group
														{selectedInfo.rowGroup.length === 1 ? '' : 's'} selected.
													</span>
												);
											} else if (actualSelectedRowCount > 0) {
												// Case B or C: Not grouped OR grouped and expanded - show row count
												return (
													<span>
														{totalCount >= actualSelectedRowCount
															? `${formatThousands(actualSelectedRowCount)} of ${formatThousands(totalCount)} rows selected. `
															: `${formatThousands(actualSelectedRowCount)} rows selected. `}
													</span>
												);
											} else if (selectedInfo?.rowGroup?.length > 0) {
												// Fallback: If we have groups but no rows, show group count
												return (
													<span>
														{formatThousands(selectedInfo.rowGroup.length)} group
														{selectedInfo.rowGroup.length === 1 ? '' : 's'} selected.
													</span>
												);
											}
											return null;
										})()}

										{/* Option to select all */}
										<span className="select-info--action" onClick={onSelectAllClick}>
											{` Select all records.`}
										</span>
									</>
								)}
							</div>
						)}
					</StackItem>
				)}

				<StackItem>
					<FilterTags gridRef={gridRef} tableId={tableId} />
				</StackItem>

				 <StackItem isGrow isStretch>
          {loading && <Dot9loader />}
          <div style={gridStyle} className="ag-theme-alpine">
            <AgGridReact
              rowStyle={rowStyle}
              getRowStyle={getRowStyle}
              ref={gridRef}
              key={tableId}
              rowModelType={"serverSide"}
              serverSideStoreType="full"
              columnDefs={columnDefs}
              blockLoadDebounceMillis={200}
              // navigateToNextCell={navigateToNextCell} 
              defaultColDef={defaultColDef}
              serverSideInitialRowCount={PAGE_SIZE}
              cacheBlockSize={PAGE_SIZE}
              purgeClosedRowNodes={false}
              maxBlocksInCache={10}
              onGridReady={onGridReady}
              rowHeight={32}
              headerHeight={32}
              tabToNextCell={tabToNextCell}
              focusGridInnerElement={(
                params: FocusGridInnerElementParams<TData>
              ) => true}
              detailRowHeight={500}
              sideBar={sideBar}
              rowSelection={rowSelection}
              onSelectionChanged={_.debounce(onSelectionChanged, 100)}
              isRowSelectable={isRowSelectable}
              defaultExportParams={{
                fileName: "Skulicity",
                sheetName: "Skulicity Export",
              }}
              rowGroupPanelShow={rowGroupPanelShow && "always"}
              groupDisplayType={"multipleColumns"}
              suppressGroupRowsSticky={true}
              onModelUpdated={onModelUpdated}
              detailCellRendererParams={detailCellRendererParams}
              masterDetail={Boolean(gridMeta?.ChildTableID)}
              columnTypes={columnTypes}
               theme={theme}
              groupSelectsChildren={false}
              enableTextSelection={true}
              maxConcurrentDatasourceRequests={1}
              autoGroupColumnDef={autoGroupColumnDef}
              suppressRowClickSelection={true}
              pivotPanelShow={rowPivotPanelShow && "always"}
              suppressAggFuncInHeader={!rowPivotPanelShow}
              suppressExpandablePivotGroups={true}
              pivotMode={pivotMode}
              onColumnMoved={onColumnMoved}
              onColumnPinned={onColumnPinned}
              onColumnResized={handleColumnResized}
              rowBuffer={20}
              onColumnVisible={onColumnVisible}
              getContextMenuItems={getContextMenuItems}
              onViewportChanged={onViewportChanged}
              noRowsOverlayComponent={Empty}
              onCellValueChanged={onUpdateCell}
              getMainMenuItems={getMainMenuItems}
              onColumnPivotModeChanged={onColumnPivotModeChanged}
              onRowGroupOpened={onRowGroupOpened}
              stopEditingWhenCellsLoseFocus={true}
              suppressHeaderFocus={false}
              suppressFlash={true}
              animateRows={false}
              debounceVerticalScrollbar={true}
              immutableData={true}
              onFirstDataRendered={onFirstDataRendered}
              onCellKeyDown={onCellKeyDown}
            />
          </div>
        </StackItem>
			</Stack>
		</div>
	);
};

export default AgGrid;
